import {useQueryParamValue} from '@hconnect/common/query-params'
import {plantTimezonedDateRangeValidator} from '@hconnect/common/utils'
import {useUpmPlantTimezone} from '@hconnect/upm'
import {
  AllDesktopFilterOptionsKeys,
  AllFilterOptions,
  desktopFilterOptions,
  FilterOptions,
  filterSettingsToUrl,
  Page,
  pmNotificationFilterOptions,
  sortingFilterParams,
  sortingNotificationsFilterParams,
  sortingWorkOrdersFilterParams,
  urlToFilterSettings,
  workOrderFilterOptions
} from '@widgets/shift-event-filters'
import {isUndefined, omit, omitBy, pick} from 'lodash'
import {useCallback, useMemo, useState} from 'react'

const EVENT_FILTERS = 'eventsFilters'

/**
 * Pages that belong to the shift events tab (Events page with tabs)
 */
export type ShiftEventsPage = Page.Events | Page.WorkOrders | Page.Notifications

/**
 * Filter keys for each shift events page type
 */
export const shiftEventsFilterPageKeys: Record<ShiftEventsPage, AllDesktopFilterOptionsKeys[]> = {
  [Page.Events]: [...desktopFilterOptions, ...sortingFilterParams],
  [Page.WorkOrders]: [...workOrderFilterOptions, ...sortingWorkOrdersFilterParams],
  [Page.Notifications]: [...pmNotificationFilterOptions, ...sortingNotificationsFilterParams]
}

/**
 * Get scoped filters for shift events pages (Events, WorkOrders, Notifications)
 */
export const getShiftEventsPageScopedFilters = (
  filters: string,
  page: ShiftEventsPage,
  timezone: string,
  defaultFilters?: Partial<FilterOptions>
): FilterOptions => {
  const filterOptions: FilterOptions = urlToFilterSettings(new URLSearchParams(filters), timezone)
  return {
    ...defaultFilters,
    ...omitBy(pick(filterOptions, ['timeRange', ...shiftEventsFilterPageKeys[page]]), isUndefined)
  }
}

type ShiftEventsPageFiltersProps = [FilterOptions, (filterOptions: FilterOptions) => void]

/**
 * Hook for managing URL query parameters and filters for shift events pages
 * (Events, WorkOrders, Notifications tabs on the Events page)
 *
 * @param plantId - The plant ID
 * @param page - The shift events page type
 * @param defaultFilters - Optional default filter values
 * @returns Tuple of [current filters, setter function]
 */
export const useShiftEventsPageFilters = (
  plantId: string,
  page: ShiftEventsPage,
  defaultFilters?: Partial<AllFilterOptions>
): ShiftEventsPageFiltersProps => {
  const timezone = useUpmPlantTimezone(plantId)
  const keys = useMemo(() => ['timeRange', ...shiftEventsFilterPageKeys[page]], [page])
  const [eventFilters = '', setEventFilters] = useQueryParamValue(EVENT_FILTERS, keys, {
    keepInLocalStorage: true
  })

  const [filters, setFilters] = useState<FilterOptions>(
    getShiftEventsPageScopedFilters(eventFilters, page, timezone, defaultFilters)
  )

  const setFilterValue = useCallback(
    (filterOptions: FilterOptions) => {
      if (Object.values(filterOptions).every(isUndefined)) {
        setEventFilters('')
        setFilters({})
        return
      }
      const filtersWithoutFreeText = omit(filterOptions, 'freeText')
      plantTimezonedDateRangeValidator(timezone, filtersWithoutFreeText.timeRange)
      setEventFilters(filterSettingsToUrl(filtersWithoutFreeText))
      setFilters(filterOptions)
    },
    [setEventFilters, timezone]
  )

  return [filters, setFilterValue]
}
